import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, CheckCircle2, ExternalLink, FileImage, FolderOpen, Loader2, MonitorUp, RotateCcw, Trash2, UploadCloud, WifiOff, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { useSelectedWorkspace } from '../../stores/workspaceStore';
import { currentSession } from '../../auth/passwordSession';
import { getConfig } from '../../config/runtimeConfig';
import { getStudyRef } from '../../features/collections/api';
import { droppedFiles } from '../../features/upload/fileHandles';
import { MetadataForm } from './MetadataForm';
import { HISTORY_DAYS, canConfirm, filePct, isMoving, useEnterpriseUpload, type UploadRow } from './store';

/** Vendor names for the drop-zone formats line; unknown extensions fall back
 *  to their bare ".ext". Zip is rendered separately as the MIRAX vehicle. */
const FORMAT_BRANDS: Record<string, string> = {
  svs: 'Aperio',
  ndpi: 'Hamamatsu',
  tif: 'TIFF',
  tiff: 'TIFF',
  scn: 'Leica',
  dcm: 'DICOM',
  jpg: 'JPEG',
  jpeg: 'JPEG',
  png: 'PNG',
};

function imageFormatLine(exts: string[]): string {
  const groups: { brand: string; exts: string[]; known: boolean }[] = [];
  for (const e of exts) {
    if (e === 'zip') continue;
    const brand = FORMAT_BRANDS[e] ?? `.${e}`;
    const g = groups.find((x) => x.brand === brand);
    if (g) g.exts.push(e);
    else groups.push({ brand, exts: [e], known: e in FORMAT_BRANDS });
  }
  return groups.map((g) => (g.known ? `${g.brand} (${g.exts.map((e) => `.${e}`).join(' ')})` : g.brand)).join(', ');
}

function fmtBytes(n: number): string {
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(1)} GB`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${n} B`;
}

// Each row shows two labeled tracks — the FILE (its bytes reaching the server)
// and the DETAILS (saved, confirmed, or awaited from the LIS) — and, on the
// right, where the server has the image: processing, needing the person's
// confirmation, imported, or failed.

type PillTone = 'ok' | 'busy' | 'warn' | 'muted' | 'bad';

function Pill({ label, value, tone, title, icon }: { label: string; value: string; tone: PillTone; title?: string; icon?: React.ReactNode }) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] leading-4',
        tone === 'ok' && 'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
        tone === 'busy' && 'border-primary/30 bg-primary/10 text-primary',
        tone === 'warn' && 'border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400',
        tone === 'muted' && 'border-border bg-muted text-muted-foreground',
        tone === 'bad' && 'border-destructive/40 bg-destructive/10 text-destructive',
      )}
    >
      <span className="opacity-60">{label}</span>
      {icon}
      <span className="font-medium tabular-nums">{value}</span>
    </span>
  );
}

/** The FILE track: bytes moving to the server (or already there). */
function FilePill({ row, offline }: { row: UploadRow; offline: boolean }) {
  switch (row.phase) {
    case 'queued':
      return <Pill label="file" value="uploading 0%" tone="muted" />;
    case 'starting':
    case 'uploading':
    case 'finishing':
      return offline ? (
        <Pill label="file" value="offline" tone="warn" icon={<WifiOff className="h-3 w-3" />} />
      ) : (
        <Pill label="file" value={`uploading ${filePct(row)}%`} tone="busy" icon={<Loader2 className="h-3 w-3 animate-spin" />} />
      );
    case 'failed':
      return <Pill label="file" value="failed" tone="bad" title={row.error ?? undefined} icon={<AlertTriangle className="h-3 w-3" />} />;
    case 'waiting-file':
      return <Pill label="file" value={row.loaded > 0 ? `${filePct(row)}%, drop again` : 'drop again'} tone="warn" title="Drop the same file here to send it; it continues where it left off" />;
    case 'elsewhere':
      return <Pill label="file" value="another tab" tone="muted" icon={<MonitorUp className="h-3 w-3" />} title="Being uploaded from another tab or window" />;
    default:
      return <Pill label="file" value="uploaded" tone="ok" icon={<Check className="h-3 w-3" />} />;
  }
}

/** The DETAILS track: saved as typed, then confirmed by the person or the LIS. */
function DetailsPill({ row }: { row: UploadRow }) {
  if (row.stage === 'failed') return null;
  if (row.stage === 'published' || row.ready) return <Pill label="details" value="confirmed" tone="ok" icon={<Check className="h-3 w-3" />} />;
  if (row.detailsError) return <Pill label="details" value="not saved" tone="bad" title={row.detailsError.message} />;
  if (row.metadata) {
    return row.metadata.overdue ? (
      <Pill label="metadata" value="overdue" tone="warn" title={row.metadata.error ?? 'The LIS has not sent this image’s metadata in time. Fill in the details and confirm to import it.'} />
    ) : (
      <Pill label="metadata" value="waiting" tone="muted" title="Waiting for the LIS; or fill in the details and confirm" />
    );
  }
  if (row.dirty && row.imageId) return <Pill label="details" value="saving" tone="muted" />;
  return <Pill label="details" value="to confirm" tone="warn" title="Review the details and click Confirm details: nothing is imported without it" />;
}

/** The study page link: the enterprise study route is addressed by UID, and the list names the study by public id. */
function StudyLink({ studyId }: { studyId: string }) {
  const { data } = useQuery({ queryKey: ['study-ref', studyId], queryFn: () => getStudyRef(studyId), staleTime: Infinity });
  if (!data) return null;
  const base = import.meta.env.BASE_URL.replace(/\/+$/, '');
  return (
    <a
      href={`${base}/studies/${encodeURIComponent(data.study_instance_uid)}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      title="Open in a new tab"
      className="rounded p-0.5 hover:bg-accent hover:opacity-80"
    >
      <ExternalLink className="h-3.5 w-3.5" />
    </a>
  );
}

/** Where the server has the image, and the one thing the person can do about it. */
function OutcomeCell({ row }: { row: UploadRow }) {
  const confirm = useEnterpriseUpload((s) => s.confirm);
  if (row.stage === 'published') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400">
        <Check className="h-3.5 w-3.5" />
        Imported
        {row.study && <StudyLink studyId={row.study.public_id} />}
      </span>
    );
  }
  if (row.stage === 'failed') {
    return (
      <span className="inline-flex min-w-0 items-center gap-1 text-xs text-destructive" title={row.stageError ?? undefined}>
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{row.stageError ?? 'could not be imported'}</span>
      </span>
    );
  }
  if (row.phase !== 'done') return <span className="text-xs text-muted-foreground/50">...</span>;
  if (row.stage === 'needs-details' && !row.ready && !row.metadata) {
    return (
      <span className="inline-flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400">
        needs details
        <Button
          size="sm"
          className="h-6 px-2 text-xs"
          disabled={row.confirming || !canConfirm(row)}
          onClick={(e) => {
            e.stopPropagation();
            void confirm(row.id);
          }}
        >
          {row.confirming ? <Loader2 className="animate-spin" /> : <CheckCircle2 />}
          Confirm
        </Button>
      </span>
    );
  }
  if (row.stage === 'needs-details' && row.metadata) {
    return (
      <span className={cn('inline-flex items-center gap-1 text-xs', row.metadata.overdue ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground')}>
        {row.metadata.overdue ? <AlertTriangle className="h-3.5 w-3.5" /> : <Loader2 className="h-3.5 w-3.5 animate-spin" />}
        {row.metadata.overdue ? 'metadata overdue' : 'waiting for metadata'}
      </span>
    );
  }
  const label = row.stage === 'needs-details' ? 'importing' : `importing ${Math.max(0, Math.min(99, row.percent ?? 0))}%`;
  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
      <Loader2 className="h-3.5 w-3.5 animate-spin" /> {label}
    </span>
  );
}

function ProgressBar({ row }: { row: UploadRow }) {
  const transferring = isMoving(row);
  const processing = row.phase === 'done' && (row.stage === 'converting' || row.stage === 'ingesting');
  if (!transferring && !processing) return null;
  const pct = transferring ? filePct(row) : Math.max(0, Math.min(99, row.percent ?? 0));
  return (
    <div className="h-1 w-full overflow-hidden rounded-full bg-secondary">
      <div className={cn('h-full rounded-full transition-[width] duration-200', transferring ? 'bg-primary' : 'bg-primary/40')} style={{ width: `${pct}%` }} />
    </div>
  );
}

/**
 * "You have nowhere to put these", said BEFORE the files are chosen.
 *
 * The server refuses an upload with no write target, and that refusal is
 * correct — it will not guess which workspace a slide belongs in. But it
 * arrives after the person has selected files; the session already carries
 * the reason, so this moves the news to the top of the page with the fix one
 * click away. Renders nothing in the ordinary case, and nothing where there is
 * no auth to have a workspace under.
 */
function NoUploadTarget(): JSX.Element | null {
  const { data } = useQuery({
    // The same key AccountControl uses, so this shares one fetch.
    queryKey: ['auth', 'session'],
    queryFn: currentSession,
    enabled: getConfig().auth !== null,
    staleTime: Infinity,
    retry: false,
  });
  if (!data?.writeDenied) return null;
  return (
    <div role="status" className="flex shrink-0 items-start gap-2.5 rounded-md border border-primary/30 bg-primary/5 px-3.5 py-3 text-sm">
      <FolderOpen className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
      <p className="text-foreground">
        {data.writeDenied.charAt(0).toUpperCase() + data.writeDenied.slice(1)}.{' '}
        <Link to="/workspaces" className="font-medium text-primary underline underline-offset-2">
          Go to workspaces
        </Link>
      </p>
    </div>
  );
}

function useOnline(): boolean {
  const [online, setOnline] = useState(() => typeof navigator === 'undefined' || navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return online;
}

export function UploadPage() {
  const { current, ready } = useSelectedWorkspace();
  const setTargetWorkspace = useEnterpriseUpload((s) => s.setTargetWorkspace);
  const allRows = useEnterpriseUpload((s) => s.rows);
  const selectedId = useEnterpriseUpload((s) => s.selectedId);
  const notices = useEnterpriseUpload((s) => s.notices);
  const acceptedExts = useEnterpriseUpload((s) => s.acceptedExts);
  const addFiles = useEnterpriseUpload((s) => s.addFiles);
  const select = useEnterpriseUpload((s) => s.select);
  const retry = useEnterpriseUpload((s) => s.retry);
  const remove = useEnterpriseUpload((s) => s.remove);
  const clearNotices = useEnterpriseUpload((s) => s.clearNotices);
  const clearMessage = useEnterpriseUpload((s) => s.clearMessage);
  const ensureConfig = useEnterpriseUpload((s) => s.ensureConfig);
  const loadHistory = useEnterpriseUpload((s) => s.loadHistory);
  const online = useOnline();

  const [dragOver, setDragOver] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const workspace = current?.public_id ?? null;

  // THE HEADER'S WORKSPACE, pushed into the store: new images are made in it,
  // and the history listed is its own. Resolved here because resolution needs
  // the live membership list.
  useEffect(() => {
    if (!ready) return;
    setTargetWorkspace(workspace);
  }, [ready, workspace, setTargetWorkspace]);

  useEffect(() => {
    void ensureConfig();
  }, [ensureConfig]);

  // Asked again WHEN THE WORKSPACE CHANGES, and only once it is known, so the
  // first answer is already the right workspace's.
  useEffect(() => {
    if (!ready) return;
    void loadHistory();
  }, [loadHistory, ready, workspace]);

  const accept = acceptedExts ? [...acceptedExts.map((e) => `.${e}`), '.json'].join(',') : undefined;
  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      // A dropped folder gives its files (a scanner's export is a folder per slide).
      void droppedFiles(e.dataTransfer, accept ?? '').then((files) => addFiles(files));
    },
    [addFiles, accept],
  );

  // The rows of the workspace in the header; a row made before one was chosen went to the default and shows everywhere.
  const rows = allRows.filter((r) => r.workspace === null || r.workspace === workspace);
  const selected = rows.find((r) => r.id === selectedId) ?? null;
  const queueOrder = rows.filter((r) => r.phase === 'queued').map((r) => r.id);

  const renderRow = (row: UploadRow) => {
    const queuePos = queueOrder.indexOf(row.id);
    return (
      <div
        key={row.id}
        onClick={() => select(row.id)}
        className={cn(
          'flex cursor-pointer flex-col gap-1.5 rounded-md border px-3 py-2 transition-colors',
          selectedId === row.id ? 'border-primary bg-accent/50' : 'hover:bg-accent/30',
        )}
      >
        <div className="flex items-center gap-2.5">
          <FileImage className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate text-sm" title={row.name}>
            {row.name}
            {row.size > 0 && <span className="ml-2 text-xs tabular-nums text-muted-foreground">{row.members?.length ? `${row.members.length} files · ` : ''}{fmtBytes(row.size)}</span>}
            {row.study?.accession_number && <span className="ml-2 text-xs text-muted-foreground">{row.study.accession_number}</span>}
            {row.phase === 'queued' && queuePos > 0 && <span className="ml-2 text-xs text-muted-foreground">#{queuePos + 1} in queue</span>}
          </span>

          <FilePill row={row} offline={!online} />
          <DetailsPill row={row} />

          <span className="flex w-48 shrink-0 justify-end text-right">
            <OutcomeCell row={row} />
          </span>

          <span className="flex w-14 shrink-0 items-center justify-end gap-0.5">
            {row.phase === 'failed' && row.file && (
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                title="Retry"
                onClick={(e) => {
                  e.stopPropagation();
                  retry(row.id);
                }}
              >
                <RotateCcw />
              </Button>
            )}
            {row.stage !== 'published' && (
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                title={row.imageId ? 'Cancel and delete this upload' : 'Remove from the list'}
                onClick={(e) => {
                  e.stopPropagation();
                  void remove(row.id);
                }}
              >
                <Trash2 />
              </Button>
            )}
          </span>
        </div>
        <ProgressBar row={row} />
        {row.phase === 'failed' && row.error && <p className="text-xs text-destructive">{row.error}</p>}
        {row.phase === 'waiting-file' && (
          <p className="text-xs text-amber-700 dark:text-amber-400">
            Its file {row.loaded > 0 ? 'did not finish uploading' : 'was not uploaded'}. Drop {row.name} here again to {row.loaded > 0 ? 'continue where it left off' : 'send it'}.
          </p>
        )}
        {row.confirmError && (
          <div className="flex items-center gap-2 rounded-md bg-amber-500/10 px-2.5 py-1.5 text-xs text-amber-700 dark:text-amber-400">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            <span className="flex-1">{row.confirmError}</span>
            <button
              aria-label="Dismiss"
              onClick={(e) => {
                e.stopPropagation();
                clearMessage(row.id);
              }}
              className="hover:opacity-70"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
    );
  };

  // Anywhere on the page accepts a drop. A counter tracks nested enter/leave
  // events so moving over children does not flicker the overlay off.
  const dragDepth = useRef(0);

  return (
    <div
      className="relative flex h-full min-h-0 gap-4 p-4"
      onDragEnter={(e) => {
        e.preventDefault();
        dragDepth.current += 1;
        if (e.dataTransfer.types.includes('Files')) setDragOver(true);
      }}
      onDragOver={(e) => e.preventDefault()}
      onDragLeave={() => {
        dragDepth.current -= 1;
        if (dragDepth.current <= 0) {
          dragDepth.current = 0;
          setDragOver(false);
        }
      }}
      onDrop={(e) => {
        dragDepth.current = 0;
        onDrop(e);
      }}
    >
      {dragOver && (
        <div className="pointer-events-none absolute inset-2 z-40 flex items-center justify-center rounded-lg border-2 border-dashed border-primary bg-primary/10">
          <span className="rounded-md bg-background/90 px-3 py-1.5 text-sm font-medium">Drop to upload</span>
        </div>
      )}
      {/* Left: dropzone + image list */}
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <NoUploadTarget />
        <div
          role="button"
          tabIndex={0}
          onClick={() => fileInput.current?.click()}
          onKeyDown={(e) => e.key === 'Enter' && fileInput.current?.click()}
          className="flex shrink-0 cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-input px-4 py-8 text-center transition-colors hover:border-primary/50"
        >
          <UploadCloud className="h-7 w-7 text-muted-foreground" />
          <div className="text-sm font-medium">Drop images here</div>
          <div className="text-xs text-muted-foreground">
            {acceptedExts ? (
              <>
                Images: {imageFormatLine(acceptedExts)}
                {acceptedExts.includes('zip') && ', or one slide as a .zip'}
                {acceptedExts.includes('mrxs') && '. A MIRAX slide is dropped as its .mrxs file with its folder'}
                {acceptedExts.includes('dcm') && '. A slide exported as several .dcm files is one image: drop its folder, or all of its files together'}
              </>
            ) : (
              'loading accepted types…'
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            Fill in each image’s details and confirm them. A matching .json file (simple, DICOM JSON or FHIR bundle:{' '}
            <code>image.svs</code> ↔ <code>image.json</code>) or a DICOM file’s own tags fill the form in for you.
          </div>
          <input
            ref={fileInput}
            type="file"
            multiple
            accept={accept}
            className="hidden"
            onChange={(e) => {
              if (e.target.files) void addFiles([...e.target.files]);
              e.target.value = '';
            }}
          />
        </div>

        {notices.length > 0 && (
          <div className="flex items-start justify-between gap-2 rounded-md bg-amber-500/10 px-3 py-2">
            <ul className="flex flex-col gap-0.5 text-xs text-amber-700 dark:text-amber-400">
              {notices.map((n, i) => (
                <li key={i}>{n}</li>
              ))}
            </ul>
            <button onClick={clearNotices} className="text-amber-700 hover:opacity-70 dark:text-amber-400" aria-label="Dismiss notices">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {/* WHERE these go is the header's workspace: it governs this page, the
            study list and the history together. */}
        <ScrollArea className="min-h-0 flex-1">
          <div className="flex flex-col gap-1.5 pr-2">
            {rows.length === 0 && <div className="py-10 text-center text-sm text-muted-foreground">No uploads yet.</div>}
            {rows.map(renderRow)}
          </div>
        </ScrollArea>
        {rows.length > 0 && (
          <p className="shrink-0 text-center text-[11px] text-muted-foreground">Your uploads to this workspace from the last {HISTORY_DAYS} days.</p>
        )}
      </div>

      {/* Right: the details of the selected image */}
      <aside className="flex w-[30rem] shrink-0 flex-col rounded-lg border p-4">
        {selected ? (
          <MetadataForm key={selected.id} row={selected} />
        ) : (
          <div className="flex h-full items-center justify-center text-center text-sm text-muted-foreground">Select an image to review its details.</div>
        )}
      </aside>
    </div>
  );
}
